import { format } from "date-fns";
import {
  Download,
  Pause,
  Play,
  RefreshCw,
  Settings2,
} from "lucide-react";
import React from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components";
import { useI18n } from "@/context/i18n";

import type {
  TCampaignAnalyticsSummary,
  TCampaignDispatchRunRow,
} from "../../campaign-details.types";
import type { TCampaign } from "../../campaigns.types";

export interface ICampaignDashboardProps {
  campaign: TCampaign | null;
  summary: TCampaignAnalyticsSummary | undefined;
  runs: TCampaignDispatchRunRow[];
  isSummaryLoading: boolean;
  isRunsLoading: boolean;
  funnelDays: number;
  onFunnelDaysChange: (days: number) => void;
  onRefreshAnalytics: () => void;
  onExportRuns: () => void;
  onPauseCampaign: () => void;
  onResumeCampaign: () => void;
  isPausePending?: boolean;
  isResumePending?: boolean;
  onAdjustCosts: () => void;
  onChangeTemplate: () => void;
  onNewBatch: () => void;
}

function formatBRLFromCents(cents: number | null | undefined): string {
  if (cents == null) {
    return "—";
  }
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function pctLabel(n: number | null | undefined, digits = 0): string {
  if (n == null || Number.isNaN(n)) {
    return "—";
  }
  return n.toFixed(digits);
}

export const CampaignDashboard: React.FC<ICampaignDashboardProps> = ({
  campaign,
  summary,
  runs,
  isSummaryLoading,
  isRunsLoading,
  funnelDays,
  onFunnelDaysChange,
  onRefreshAnalytics,
  onExportRuns,
  onPauseCampaign,
  onResumeCampaign,
  isPausePending = false,
  isResumePending = false,
  onAdjustCosts,
  onChangeTemplate,
  onNewBatch,
}) => {
  const { t } = useI18n();
  const [runSearch, setRunSearch] = React.useState("");

  const sent = summary?.sent_success ?? 0;
  const delivered = summary?.delivered_count ?? 0;
  const chats = summary?.chats_started ?? 0;
  const funnelMax = Math.max(sent, 1);
  const deliveryRateStr = pctLabel(summary?.delivery_rate_percent ?? null, 0);
  const chatConvStr = pctLabel(summary?.chat_conversion_percent ?? null, 0);

  const unitCostDisplay = formatBRLFromCents(campaign?.whatsapp_unit_cost_cents);
  const totalCostDisplay = formatBRLFromCents(
    summary?.estimated_total_cost_cents
  );
  const projectedExtra = Math.round(sent * 1.2);
  const unit = campaign?.whatsapp_unit_cost_cents ?? 0;
  const projectionCents =
    summary?.unit_cost_cents != null ? projectedExtra * unit : null;
  const projectionDisplay = formatBRLFromCents(projectionCents);

  const budgetUsedStr = pctLabel(summary?.budget_used_percent ?? null, 0);

  const filteredRuns = React.useMemo(() => {
    const q = runSearch.trim().toLowerCase();
    if (!q) {
      return runs;
    }
    return runs.filter((r) => {
      const tmpl = `${r.template_name ?? ""} ${r.template_language ?? ""}`.toLowerCase();
      return (
        r.batch_label.toLowerCase().includes(q) ||
        tmpl.includes(q)
      );
    });
  }, [runs, runSearch]);

  const plansLabel =
    campaign && campaign.plans.length > 0
      ? campaign.plans.map((p) => p.name).join(", ")
      : "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {campaign?.name ?? t("campaigns.details.emptyCampaign")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("campaigns.dashboard.headerPeriod").replace(
              "{{days}}",
              String(funnelDays)
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-muted-foreground">
            <span>
              {t("campaigns.details.info.plans")}: {plansLabel}
            </span>
            <span aria-hidden>·</span>
            <span>{t("campaigns.details.info.channel")}:</span>
            <Badge className="text-xs">
              {campaign ? t(`campaigns.channel.${campaign.channel}`) : "—"}
            </Badge>
            <span aria-hidden>·</span>
            <span>{t("campaigns.details.info.status")}:</span>
            {campaign && (
              <Badge
                className={
                  campaign.status === "active"
                    ? "border border-emerald-300 bg-emerald-100 text-emerald-800"
                    : ""
                }
              >
                {t(`campaigns.status.${campaign.status}`)}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          {campaign?.whatsapp_unit_cost_cents != null &&
            campaign.whatsapp_unit_cost_cents > 0 && (
              <Badge
                variant="secondary"
                className="bg-violet-600/15 text-violet-800 dark:text-violet-200 border-violet-500/30"
              >
                {t("campaigns.dashboard.unitCostBadge").replace(
                  "{{value}}",
                  unitCostDisplay
                )}
              </Badge>
            )}
          <Button type="button" variant="outline" size="sm" onClick={onExportRuns}>
            <Download className="h-4 w-4 mr-2" />
            {t("campaigns.dashboard.export")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={onPauseCampaign}
            disabled={
              !campaign ||
              campaign.status !== "active" ||
              isPausePending ||
              isResumePending
            }
          >
            <Pause className="h-4 w-4 mr-2" />
            {isPausePending ? t("generic.loading") : t("campaigns.dashboard.pause")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-emerald-700 hover:text-emerald-800 dark:text-emerald-400"
            onClick={onResumeCampaign}
            disabled={
              !campaign ||
              campaign.status !== "paused" ||
              isPausePending ||
              isResumePending
            }
          >
            <Play className="h-4 w-4 mr-2" />
            {isResumePending ? t("generic.loading") : t("campaigns.dashboard.resume")}
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={onAdjustCosts}
            disabled={!campaign}
          >
            <Settings2 className="h-4 w-4 mr-2" />
            {t("campaigns.dashboard.adjustSettings")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="shadow-sm border-border/80">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              {t("campaigns.dashboard.kpi.sent")}
            </p>
            <p className="text-3xl font-bold tabular-nums">
              {isSummaryLoading ? "…" : sent.toLocaleString("pt-BR")}
            </p>
            <div className="flex flex-wrap gap-2 mt-2 items-center">
              <Badge variant="outline" className="text-emerald-700 border-emerald-200">
                {t("campaigns.dashboard.kpi.sentToday").replace(
                  "{{count}}",
                  String(summary?.sent_today ?? 0)
                )}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {t("campaigns.details.dispatch.successFailed")}:{" "}
                {summary?.sent_failed ?? 0}
              </span>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-border/80">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              {t("campaigns.dashboard.kpi.delivered")}
            </p>
            <p className="text-3xl font-bold tabular-nums">
              {isSummaryLoading ? "…" : delivered.toLocaleString("pt-BR")}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {t("campaigns.dashboard.kpi.deliveryRate").replace(
                "{{value}}",
                deliveryRateStr
              )}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-border/80">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              {t("campaigns.dashboard.kpi.chats")}
            </p>
            <p className="text-3xl font-bold tabular-nums">
              {isSummaryLoading ? "…" : chats.toLocaleString("pt-BR")}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {t("campaigns.dashboard.kpi.chatConversion").replace(
                "{{value}}",
                chatConvStr
              )}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-border/80">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              {t("campaigns.dashboard.kpi.totalCost")}
            </p>
            <p className="text-2xl font-bold tabular-nums">
              {isSummaryLoading ? "…" : totalCostDisplay}
            </p>
            {projectionCents != null && (
              <p className="text-xs mt-2 rounded-md bg-amber-500/10 text-amber-900 dark:text-amber-200 px-2 py-1 inline-block">
                {t("campaigns.dashboard.kpi.projection").replace(
                  "{{value}}",
                  projectionDisplay
                )}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle>{t("campaigns.dashboard.funnel.title")}</CardTitle>
                <p className="text-sm text-muted-foreground font-normal">
                  {t("campaigns.dashboard.funnel.subtitle")}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  value={funnelDays}
                  onChange={(e) => onFunnelDaysChange(Number(e.target.value))}
                >
                  <option value={7}>{t("campaigns.dashboard.funnel.range7")}</option>
                  <option value={30}>{t("campaigns.dashboard.funnel.range30")}</option>
                  <option value={90}>{t("campaigns.dashboard.funnel.range90")}</option>
                </select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onRefreshAnalytics}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t("campaigns.dashboard.funnel.refresh")}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { label: t("campaigns.dashboard.funnel.sent"), value: sent },
                {
                  label: t("campaigns.dashboard.funnel.delivered"),
                  value: delivered,
                },
                { label: t("campaigns.dashboard.funnel.chats"), value: chats },
              ].map((row) => (
                <div key={row.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{row.label}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {row.value.toLocaleString("pt-BR")}
                    </span>
                  </div>
                  <div className="h-3 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-violet-600 transition-all"
                      style={{
                        width: `${Math.min(100, (row.value / funnelMax) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>{t("campaigns.dashboard.runs.title")}</CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                {t("campaigns.dashboard.runs.subtitle")}
              </p>
              <Input
                className="max-w-md mt-2"
                placeholder={t("campaigns.dashboard.runs.searchPlaceholder")}
                value={runSearch}
                onChange={(e) => setRunSearch(e.target.value)}
              />
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("campaigns.dashboard.runs.colDate")}</TableHead>
                    <TableHead>{t("campaigns.dashboard.runs.colBatch")}</TableHead>
                    <TableHead>{t("campaigns.dashboard.runs.colTemplate")}</TableHead>
                    <TableHead className="text-right">
                      {t("campaigns.dashboard.runs.colSent")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("campaigns.dashboard.runs.colDelivered")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("campaigns.dashboard.runs.colChats")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("campaigns.dashboard.runs.colCost")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isRunsLoading && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-muted-foreground">
                        {t("generic.loading")}
                      </TableCell>
                    </TableRow>
                  )}
                  {!isRunsLoading && filteredRuns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-muted-foreground">
                        {t("campaigns.dashboard.na")}
                      </TableCell>
                    </TableRow>
                  )}
                  {!isRunsLoading &&
                    filteredRuns.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap">
                          {format(new Date(r.created_at), "dd/MM/yyyy HH:mm")}
                        </TableCell>
                        <TableCell className="font-medium">
                          {r.batch_label}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {r.template_name ?? t("campaigns.dashboard.na")}
                          {r.template_language
                            ? ` · ${r.template_language}`
                            : ""}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.sent_items}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.delivered_items}
                          {r.sent_items > 0
                            ? ` (${Math.round((r.delivered_items / r.sent_items) * 100)}%)`
                            : ""}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.chats_started}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatBRLFromCents(r.estimated_cost_cents)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>{t("campaigns.dashboard.costs.title")}</CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                {t("campaigns.dashboard.costs.subtitle")}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border p-3 bg-muted/30">
                <p className="text-xs text-muted-foreground">
                  {t("campaigns.dashboard.costs.perDispatch")}
                </p>
                <p className="text-lg font-semibold">{unitCostDisplay}</p>
              </div>
              <div className="rounded-lg border p-3 bg-muted/30">
                <p className="text-xs text-muted-foreground">
                  {t("campaigns.dashboard.costs.budget")}
                </p>
                <p className="text-lg font-semibold">
                  {formatBRLFromCents(campaign?.whatsapp_budget_cents)}
                </p>
              </div>
              {summary?.budget_cents != null && summary.budget_cents > 0 && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>
                      {t("campaigns.dashboard.costs.used").replace(
                        "{{value}}",
                        budgetUsedStr
                      )}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-violet-600"
                      style={{
                        width: `${Math.min(100, summary.budget_used_percent ?? 0)}%`,
                      }}
                    />
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {t("campaigns.dashboard.costs.tip")}
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>{t("campaigns.dashboard.actions.title")}</CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                {t("campaigns.dashboard.actions.subtitle")}
              </p>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <Button type="button" variant="outline" disabled>
                {t("campaigns.dashboard.actions.retryFailed")}
              </Button>
              <Button type="button" variant="outline" onClick={onChangeTemplate}>
                {t("campaigns.dashboard.actions.changeTemplate")}
              </Button>
              <Button
                type="button"
                className="bg-violet-600 hover:bg-violet-700 text-white w-full"
                onClick={onNewBatch}
              >
                {t("campaigns.dashboard.actions.newBatch")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
